import React, { useEffect, useState } from "react";
import { studentService } from "../../firebase/services";
import {
  Users,
  Search,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  Shield,
  User,
  Power,
  GraduationCap,
} from "lucide-react";

export const AdminStudents = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [migratingIds, setMigratingIds] = useState(false);

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const list = await studentService.getStudents();
      setStudents(list);
    } catch (err) {
      console.error("Fetch students error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents();
  }, []);

  const handleMigrateIds = async () => {
    if (!window.confirm("Convert existing Student IDs to KIAN-CYBER-XXXX format? Attendance history will be re-keyed safely.")) return;
    setMigratingIds(true);
    try {
      const result = await studentService.migrateStudentIdsToKianFormat();
      alert(result.changed ? `${result.changed} Student ID(s) updated successfully.` : "All Student IDs are already in the new format.");
      await fetchStudents();
    } catch (err) {
      console.error("Student ID migration error:", err);
      alert(err.message || "Could not migrate Student IDs.");
    } finally {
      setMigratingIds(false);
    }
  };

  const handleToggleStatus = async (student) => {
    const action = student.status === "active" ? "deactivate" : "activate";
    if (!window.confirm(`Are you sure you want to ${action} student ${student.fullName}?`)) {
      return;
    }

    setActionLoading(true);
    try {
      await studentService.toggleStudentStatus(student.id, student.status);
      fetchStudents();
    } catch (err) {
      console.error("Toggle student status error:", err);
      alert(err.message || "Failed to change student status.");
    } finally {
      setActionLoading(false);
    }
  };

  const filteredStudents = students.filter((s) => {
    const q = searchQuery.toLowerCase();
    return (
      (s.fullName && s.fullName.toLowerCase().includes(q)) ||
      (s.studentId && s.studentId.toLowerCase().includes(q)) ||
      (s.email && s.email.toLowerCase().includes(q)) ||
      (s.college && s.college.toLowerCase().includes(q))
    );
  });

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border pb-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Enrolled Masterclass Students</h1>
          <p className="text-xs text-muted-foreground font-mono">
            Active and deactivated student roster with verified Student IDs.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 self-start sm:self-auto">
          <button onClick={handleMigrateIds} disabled={migratingIds} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 text-xs font-mono disabled:opacity-50">
            {migratingIds ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Shield className="w-3.5 h-3.5" />}
            {migratingIds ? "Updating IDs..." : "Convert IDs to KIAN-CYBER"}
          </button>
          <div className="text-xs font-mono px-3 py-1.5 rounded-lg bg-card border border-border text-muted-foreground">
            Total Enrolled: <strong className="text-emerald-400">{students.length}</strong>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-card border border-border rounded-xl p-4">
        <div className="relative">
          <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-2.5" />
          <input
            type="text"
            data-testid="admin-student-search-input"
            placeholder="Search by Student ID (e.g. KIAN-CYBER-0001), name, or college..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-1.5 text-xs rounded-lg bg-background border border-input focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono"
          />
        </div>
      </div>

      {/* Students Table */}
      <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-muted-foreground flex flex-col items-center justify-center gap-2">
            <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
            <span className="text-xs font-mono">Loading students from Firestore...</span>
          </div>
        ) : filteredStudents.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground space-y-2">
            <AlertCircle className="w-8 h-8 mx-auto text-muted-foreground/50" />
            <div className="text-sm font-semibold text-foreground">No approved students yet.</div>
            <p className="text-xs max-w-sm mx-auto font-mono">
              Once applicant registrations are approved in <code>/admin/registrations</code>, students will appear here.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs font-mono">
              <thead className="bg-muted/50 border-b border-border text-muted-foreground uppercase text-[10px]">
                <tr>
                  <th className="px-4 py-3">Student ID</th>
                  <th className="px-4 py-3">Student Name</th>
                  <th className="px-4 py-3">Registered Email</th>
                  <th className="px-4 py-3">College & Degree</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredStudents.map((st) => (
                  <tr key={st.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3 font-bold text-emerald-400">
                      {st.studentId}
                    </td>

                    <td className="px-4 py-3 font-semibold text-foreground font-sans">
                      <div className="flex items-center space-x-2">
                        {st.photoUrl ? (
                          <img
                            src={st.photoUrl}
                            alt="Student"
                            className="w-7 h-7 rounded object-cover border border-border flex-shrink-0"
                          />
                        ) : (
                          <div className="w-7 h-7 rounded bg-muted flex items-center justify-center text-muted-foreground text-[10px]">
                            {st.fullName ? st.fullName.charAt(0) : "S"}
                          </div>
                        )}
                        <span>{st.fullName}</span>
                      </div>
                    </td>

                    <td className="px-4 py-3 text-muted-foreground">{st.email}</td>

                    <td className="px-4 py-3">
                      <div className="text-foreground truncate max-w-[200px]">{st.college}</div>
                      <div className="text-muted-foreground text-[11px]">{st.course}</div>
                    </td>

                    <td className="px-4 py-3">
                      {st.status === "active" ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-semibold">
                          <CheckCircle2 className="w-3 h-3" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono bg-rose-500/10 text-rose-400 border border-rose-500/20 font-semibold">
                          <XCircle className="w-3 h-3" />
                          Deactivated
                        </span>
                      )}
                    </td>

                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end space-x-1.5">
                        <button
                          onClick={() => setSelectedStudent(st)}
                          data-testid={`view-student-${st.id}`}
                          className="px-2.5 py-1 rounded bg-muted hover:bg-muted/80 text-foreground border border-border text-[11px] transition-colors"
                        >
                          Profile
                        </button>
                        <button
                          onClick={() => handleToggleStatus(st)}
                          disabled={actionLoading}
                          data-testid={`toggle-student-${st.id}`}
                          className={`px-2.5 py-1 rounded text-[11px] font-mono flex items-center gap-1 transition-colors ${
                            st.status === "active"
                              ? "bg-rose-600/10 hover:bg-rose-600/20 text-rose-400 border border-rose-500/20"
                              : "bg-emerald-600/10 hover:bg-emerald-600/20 text-emerald-400 border border-emerald-500/20"
                          }`}
                        >
                          <Power className="w-3 h-3" />
                          <span>{st.status === "active" ? "Deactivate" : "Activate"}</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Student Profile Modal */}
      {selectedStudent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-card border border-border max-w-lg w-full rounded-2xl p-6 shadow-2xl space-y-6">
            <div className="flex items-center justify-between border-b border-border pb-3">
              <div className="flex items-center space-x-2">
                <Shield className="w-5 h-5 text-emerald-400" />
                <h3 className="font-bold text-base">Verified Student Card</h3>
              </div>
              <button
                onClick={() => setSelectedStudent(null)}
                className="text-muted-foreground hover:text-foreground text-xs font-mono"
              >
                ✕ Close
              </button>
            </div>

            <div className="flex items-center space-x-4 p-4 rounded-xl bg-muted/40 border border-border">
              {selectedStudent.photoUrl ? (
                <img
                  src={selectedStudent.photoUrl}
                  alt="Student Passport"
                  className="w-20 h-24 rounded-lg object-cover border border-border"
                />
              ) : (
                <div className="w-20 h-24 rounded-lg bg-muted border border-border flex items-center justify-center text-muted-foreground">
                  <User className="w-8 h-8 opacity-40" />
                </div>
              )}
              <div className="space-y-1 text-xs font-mono">
                <div className="text-emerald-400 font-extrabold text-lg">
                  {selectedStudent.studentId}
                </div>
                <div className="font-bold text-foreground text-sm font-sans">
                  {selectedStudent.fullName}
                </div>
                <div className="text-muted-foreground">{selectedStudent.email}</div>
                <div className="text-muted-foreground">{selectedStudent.mobile}</div>
              </div>
            </div>

            <div className="space-y-2 text-xs font-mono">
              <div className="p-3 rounded-lg bg-muted/30 border border-border flex justify-between">
                <span className="text-muted-foreground">Institute:</span>
                <span className="font-semibold text-foreground">{selectedStudent.college}</span>
              </div>
              <div className="p-3 rounded-lg bg-muted/30 border border-border flex justify-between">
                <span className="text-muted-foreground">Course & Year:</span>
                <span className="font-semibold text-foreground">
                  {selectedStudent.course} ({selectedStudent.yearSemester})
                </span>
              </div>
              <div className="p-3 rounded-lg bg-muted/30 border border-border flex justify-between">
                <span className="text-muted-foreground">City:</span>
                <span className="font-semibold text-foreground">{selectedStudent.city}</span>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <button
                onClick={() => setSelectedStudent(null)}
                className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-mono font-medium"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
